/**
 * Generate random outage periods for a given year
 * @param {number} numPeriods - Number of random periods to generate
 * @param {number} baseDuration - Base duration in hours
 * @param {number} variabilityPercent - Variability as a percentage (e.g., 20 for ±20%)
 * @param {boolean} onePerDay - If true, ensures max one event per day
 * @param {number} daysInYear - Number of days in selected year (365/366)
 * @returns {Array} Array of event objects {id, dayOfYear, startHour, duration} sorted by dayOfYear
 */
const RANDOM_UINT32_RANGE = 0x100000000;

const secureRandomUint32 = () => {
  const cryptoObject = typeof window !== "undefined" ? window.crypto : null;
  if (!cryptoObject || typeof cryptoObject.getRandomValues !== "function") {
    throw new Error("Secure randomness is unavailable in this environment.");
  }

  const values = new Uint32Array(1);
  cryptoObject.getRandomValues(values);
  return values[0];
};

const secureRandom = () => secureRandomUint32() / RANDOM_UINT32_RANGE;

const secureRandomInt = (maxExclusive) => {
  if (!Number.isInteger(maxExclusive) || maxExclusive <= 0) {
    throw new RangeError("maxExclusive must be a positive integer.");
  }

  const limit = RANDOM_UINT32_RANGE - (RANDOM_UINT32_RANGE % maxExclusive);
  let value;
  do {
    value = secureRandomUint32();
  } while (value >= limit);
  return value % maxExclusive;
};

export const generateRandomPeriods = (numPeriods, baseDuration, variabilityPercent, onePerDay, daysInYear = 365) => {
  const events = [];
  const totalDays = Number.isFinite(daysInYear) ? Math.max(1, Math.floor(daysInYear)) : 365;
  const requestedCount = Math.max(0, Math.floor(Number(numPeriods) || 0));
  const eventCount = onePerDay ? Math.min(requestedCount, totalDays) : requestedCount;

  if (eventCount === 0) {
    return events;
  }
  
  // Formula for maximum variance in hours based on the percentage
  // e.g., 5 hours * (20 / 100) = 1 hour of max variance
  const maxVarianceHours = baseDuration * (variabilityPercent / 100);

  // Keep per-day occupied windows to guarantee no overlap.
  const dayWindows = new Map(); // day -> Array<[startMin, endMin]>

  const getRandomDurationHours = () => {
    const randomShift = (secureRandom() * 2 * maxVarianceHours) - maxVarianceHours;
    return Math.max(0.5, Math.min(24, baseDuration + randomShift));
  };

  const findPlacementInDay = (day, durationMinutes) => {
    const windows = [...(dayWindows.get(day) || [])].sort((a, b) => a[0] - b[0]);
    const gapRanges = [];

    let cursor = 0;
    windows.forEach(([start, end]) => {
      if (start - cursor >= durationMinutes) {
        gapRanges.push([cursor, start - durationMinutes]);
      }
      cursor = Math.max(cursor, end);
    });

    if (1440 - cursor >= durationMinutes) {
      gapRanges.push([cursor, 1440 - durationMinutes]);
    }

    if (!gapRanges.length) return null;
    const chosenGap = gapRanges[secureRandomInt(gapRanges.length)];
    const [minStart, maxStart] = chosenGap;
    const startMin = minStart === maxStart
      ? minStart
      : minStart + secureRandomInt(maxStart - minStart + 1);
    const endMin = startMin + durationMinutes;
    return { startMin, endMin };
  };

  let selectedDays = [];
  if (onePerDay) {
    // Efficient unique-day selection without repeated retries.
    const allDays = Array.from({ length: totalDays }, (_, idx) => idx + 1);
    for (let i = allDays.length - 1; i >= allDays.length - eventCount; i -= 1) {
      const j = secureRandomInt(i + 1);
      [allDays[i], allDays[j]] = [allDays[j], allDays[i]];
      selectedDays.push(allDays[i]);
    }
  }

  for (let i = 0; i < eventCount; i++) {
    const actualDuration = getRandomDurationHours();
    const durationMinutes = Math.max(1, Math.round(actualDuration * 60));

    let chosenDay = null;
    let placement = null;

    if (onePerDay) {
      chosenDay = selectedDays[i];
      placement = findPlacementInDay(chosenDay, durationMinutes);
    } else {
      const offset = secureRandomInt(totalDays);
      for (let d = 0; d < totalDays; d++) {
        const day = ((offset + d) % totalDays) + 1;
        const candidate = findPlacementInDay(day, durationMinutes);
        if (candidate) {
          chosenDay = day;
          placement = candidate;
          break;
        }
      }
    }

    if (!placement || !chosenDay) {
      throw new Error("Unable to generate non-overlapping outages. Reduce periods, duration, or variability.");
    }

    const windows = dayWindows.get(chosenDay) || [];
    windows.push([placement.startMin, placement.endMin]);
    dayWindows.set(chosenDay, windows);

    const startHourFloat = placement.startMin / 60;

    events.push({
      id: i + 1,
      dayOfYear: chosenDay,
      startHour: startHourFloat,
      duration: actualDuration
    });
  }

  // Sort chronologically from Jan 1 to Dec 31
  return events.sort((a, b) => a.dayOfYear - b.dayOfYear);
};

/**
 * Convert day of year to approximate month
 * @param {number} dayOfYear - Day of year (1-365/366)
 * @param {number} year - Target year
 * @returns {string} Month abbreviation
 */
export const getDayOfYearMonth = (dayOfYear, year = new Date().getUTCFullYear()) => {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const isLeap = new Date(Date.UTC(year, 1, 29)).getUTCDate() === 29;
  const daysPerMonth = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  let cumulativeDays = 0;
  for (let i = 0; i < daysPerMonth.length; i++) {
    cumulativeDays += daysPerMonth[i];
    if (dayOfYear <= cumulativeDays) {
      return months[i];
    }
  }
  return "Dec";
};

/**
 * Get month boundaries (approximate day numbers where each month starts)
 * @param {number} year - Target year
 * @returns {Array} Array of {month, dayOfYear}
 */
export const getMonthBoundaries = (year = new Date().getUTCFullYear()) => {
  const boundaries = [];
  const isLeap = new Date(Date.UTC(year, 1, 29)).getUTCDate() === 29;
  const daysPerMonth = [31, isLeap ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  let cumulativeDays = 0;
  for (let i = 0; i < daysPerMonth.length; i++) {
    boundaries.push({ month: months[i], dayOfYear: cumulativeDays });
    cumulativeDays += daysPerMonth[i];
  }
  boundaries.push({ month: "End", dayOfYear: cumulativeDays });
  return boundaries;
};
